class FiltersDrawerOutsideClick {
  constructor() {
    this.initialized = false;
    this.boundHandlePointerDown = this.handlePointerDown.bind(this);
    this.boundHandleBackdropPointerDown = this.handleBackdropPointerDown.bind(this);
    this.boundHandleOutsideArea = this.handleOutsideArea.bind(this);
    this.boundSync = this.sync.bind(this);
    this.boundScheduleSync = this.scheduleSync.bind(this);
    this.syncTimer = null;
    this.outsideAreas = null;
    this.drawerObserver = null;
    this.rootObserver = null;
    this.init();
  }

  init() {
    const drawer = this.getDrawer();
    if (!drawer) return;

    if (!this.initialized) {
      document.addEventListener('pointerdown', this.boundHandlePointerDown, true);
      document.addEventListener('shopify:section:load', this.boundSync);
      // Resize fires far more often than section loads/mutations, so only this
      // listener is debounced; the layout read + getComputedStyle in sync()
      // would otherwise run on every intermediate resize frame.
      window.addEventListener('resize', this.boundScheduleSync, { passive: true });
      this.initialized = true;
    }

    this.ensureBackdrop();
    this.bindOutsideAreas();
    this.observeDrawer();
    this.sync();
  }

  getDrawer() {
    return document.querySelector('modal-dialog[data-id="filters-drawer"]');
  }

  getFilterPanel() {
    const drawer = this.getDrawer();
    if (!drawer) return null;

    return (
      drawer.querySelector('[data-content-type="filters"] .loading-overlay') ||
      drawer.querySelector('[data-content-type="filters"][data-show]') ||
      drawer.querySelector('.loading-overlay') ||
      drawer.querySelector('filter-form') ||
      drawer.querySelector('[data-target="modal-dialog.content"] .px-content')
    );
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 1023px)').matches;
  }

  isFiltersOpen() {
    const drawer = this.getDrawer();
    if (!drawer) return false;

    if (drawer.hasAttribute('data-open') || drawer.hasAttribute('data-visible')) {
      return true;
    }

    const root = document.getElementById('product-grid') || document.getElementById('search-grid');
    if (root?.hasAttribute('data-drawer-open')) {
      return true;
    }

    return Boolean(
      document.querySelector('dynamic-modal-toggle[data-id="filters-drawer"][data-open]')
    );
  }

  ensureBackdrop() {
    let backdrop = document.querySelector('[data-filter-outside-backdrop]');

    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.setAttribute('data-filter-outside-backdrop', '');
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.hidden = true;
      backdrop.className =
        'pointer-events-none fixed inset-0 z-[35] bg-black/20 lg:bg-transparent';
      document.body.appendChild(backdrop);
    }

    if (backdrop.dataset.bound !== 'true') {
      backdrop.addEventListener('pointerdown', this.boundHandleBackdropPointerDown);
      backdrop.dataset.bound = 'true';
    }

    this.backdrop = backdrop;
  }

  bindOutsideAreas() {
    // Detach listeners from the previously bound nodes first. Section reloads
    // swap these elements, so without this the old (detached) nodes keep their
    // listeners (leak) while the fresh nodes get none.
    this.unbindOutsideAreas();

    this.outsideAreas = document.querySelectorAll(
      '#collection-layout, #search-layout, [data-filter-outside-click-area], [data-product-grid], result-grid'
    );

    this.outsideAreas.forEach((area) => {
      area.addEventListener('pointerdown', this.boundHandleOutsideArea);
    });
  }

  unbindOutsideAreas() {
    if (!this.outsideAreas) return;

    this.outsideAreas.forEach((area) => {
      area.removeEventListener('pointerdown', this.boundHandleOutsideArea);
    });

    this.outsideAreas = null;
  }

  observeDrawer() {
    // Tear down any existing observers before re-observing. Section reloads can
    // replace the drawer/root nodes, so keeping the old observers would leak
    // and leave them watching detached nodes instead of the current ones.
    this.disconnectObservers();

    const drawer = this.getDrawer();
    if (drawer) {
      this.drawerObserver = new MutationObserver(this.boundSync);
      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['data-open', 'data-visible'],
      });
    }

    const root = document.getElementById('product-grid') || document.getElementById('search-grid');
    if (root) {
      this.rootObserver = new MutationObserver(this.boundSync);
      this.rootObserver.observe(root, {
        attributes: true,
        attributeFilter: ['data-drawer-open'],
      });
    }
  }

  disconnectObservers() {
    // Cancel any pending resized sync so it cannot run against detached nodes
    // while observers are being torn down during a section reload.
    window.clearTimeout(this.syncTimer);
    this.syncTimer = null;

    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
      this.drawerObserver = null;
    }

    if (this.rootObserver) {
      this.rootObserver.disconnect();
      this.rootObserver = null;
    }
  }

  scheduleSync() {
    window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(this.boundSync, 80);
  }

  sync() {
    if (!this.backdrop) this.ensureBackdrop();
    const open = this.isFiltersOpen();
    this.backdrop.hidden = !open;
    this.backdrop.style.pointerEvents =
      open && this.isMobileViewport() ? 'auto' : 'none';
    this.backdrop.setAttribute('aria-hidden', open ? 'false' : 'true');
    this.syncBodyScrollLock(open);
  }

  syncBodyScrollLock(_open) {
    // Option B: filters expand inline on mobile — never lock page scroll.
    document.documentElement.classList.remove('filters-drawer-open');
  }

  isInsideFilterPanel(targetOrEvent) {
    const drawer = this.getDrawer();
    if (!drawer) return false;

    // The filters-drawer is shared between the "Filters" and "Sort" toggles,
    // swapping its inner content between data-content-type="filters" and
    // "sort". Resolve the panel to whichever content is currently shown so a
    // click inside the sort options is not mistaken for an outside click.
    const panel =
      drawer.querySelector('[data-content-type][data-show]') ||
      drawer.querySelector('[data-target="modal-dialog.content"]') ||
      this.getFilterPanel();
    if (!panel) return false;

    const target = targetOrEvent?.target ?? targetOrEvent;
    if (target instanceof Node && panel.contains(target)) return true;

    const event = targetOrEvent?.target ? targetOrEvent : null;
    if (event && typeof event.composedPath === 'function') {
      for (const node of event.composedPath()) {
        if (node instanceof Element && panel.contains(node)) return true;
      }
    }

    if (target instanceof Element) {
      const host = target.closest(
        'filter-form, filter-tabs, filter-links, sort-by-form, accordion-group, accordion-item, price-range, .filter-tab, .filter-tab-content'
      );
      if (host && panel.contains(host)) return true;
    }

    return false;
  }

  isFilterToggle(target) {
    return Boolean(
      target.closest('dynamic-modal-toggle[data-id="filters-drawer"]')
    );
  }

  isPersistentFilterUi(target) {
    return Boolean(
      target.closest('[data-persistent-active-filters]') ||
        target.closest('[data-sidebar-filter-chips]') ||
        target.closest('filter-links')
    );
  }

  isThemeBackdrop(target) {
    return Boolean(
      target.closest('modal-toggle[data-id="filters-drawer"]')
    );
  }

  isOurBackdrop(target) {
    return Boolean(target.closest('[data-filter-outside-backdrop]'));
  }

  shouldCloseFromTarget(targetOrEvent) {
    const target = targetOrEvent?.target ?? targetOrEvent;

    if (!this.isFiltersOpen()) return false;
    if (this.isInsideFilterPanel(targetOrEvent)) return false;
    if (this.isFilterToggle(target)) return false;
    if (this.isPersistentFilterUi(target)) return false;
    if (this.isThemeBackdrop(target)) return false;
    if (this.isOurBackdrop(target)) return true;
    return true;
  }

  closeFiltersDrawer() {
    if (!this.isFiltersOpen()) return;

    const openToggle = document.querySelector(
      'dynamic-modal-toggle[data-id="filters-drawer"][data-open]'
    );
    const toggleButton = openToggle?.querySelector('button');

    if (toggleButton) {
      toggleButton.click();

      window.setTimeout(() => {
        if (this.isFiltersOpen()) {
          this.forceClose();
        } else {
          this.sync();
        }
      }, 80);

      return;
    }

    const themeBackdrop = this.getDrawer()?.querySelector(
      'modal-toggle[data-id="filters-drawer"]'
    );

    if (
      themeBackdrop &&
      window.getComputedStyle(themeBackdrop).pointerEvents !== 'none'
    ) {
      themeBackdrop.click();

      window.setTimeout(() => {
        if (this.isFiltersOpen()) {
          this.forceClose();
        } else {
          this.sync();
        }
      }, 80);

      return;
    }

    this.forceClose();
  }

  forceClose() {
    const drawer = this.getDrawer();

    drawer?.removeAttribute('data-open');
    drawer?.removeAttribute('data-visible');

    drawer
      ?.querySelector('[data-target="modal-dialog.content"]')
      ?.removeAttribute('data-open');

    document
      .querySelectorAll('dynamic-modal-toggle[data-id="filters-drawer"]')
      .forEach((toggle) => {
        toggle.removeAttribute('data-open');
        toggle.setAttribute('aria-pressed', 'false');
      });

    document.getElementById('product-grid')?.removeAttribute('data-drawer-open');
    document.getElementById('search-grid')?.removeAttribute('data-drawer-open');
    document.documentElement.classList.remove('filters-drawer-open');

    this.sync();
  }

  handleBackdropPointerDown(event) {
    if (!this.isMobileViewport()) return;
    event.preventDefault();
    event.stopPropagation();
    this.closeFiltersDrawer();
  }

  handleOutsideArea(event) {
    if (!this.shouldCloseFromTarget(event)) return;
    this.closeFiltersDrawer();
  }

  handlePointerDown(event) {
    if (!this.shouldCloseFromTarget(event)) return;
    this.closeFiltersDrawer();
  }
}

function initFiltersDrawerOutsideClick() {
  if (!document.querySelector('modal-dialog[data-id="filters-drawer"]')) return;

  if (!window.__filtersDrawerOutsideClick) {
    window.__filtersDrawerOutsideClick = new FiltersDrawerOutsideClick();
    return;
  }

  window.__filtersDrawerOutsideClick.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFiltersDrawerOutsideClick);
} else {
  initFiltersDrawerOutsideClick();
}

document.addEventListener('shopify:section:load', initFiltersDrawerOutsideClick);

if (!customElements.get('filters-drawer-outside-click')) {
  customElements.define(
    'filters-drawer-outside-click',
    class FiltersDrawerOutsideClickElement extends HTMLElement {
      connectedCallback() {
        initFiltersDrawerOutsideClick();
      }
    }
  );
}
